import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { getCurrentUser, login as loginRequest, logout as logoutRequest } from "@/api/auth";
import { api } from "@/api/client";
import { useAuthStore } from "@/auth/store";

/**
 * Beim App-Start einmal /auth/refresh anstoßen — wenn ein Refresh-Cookie da
 * ist, kommt ein neues Access-Token zurück und wir laden den User. Schlägt
 * der Refresh fehl, ist der User halt nicht eingeloggt.
 */
export function useHydrateAuth(): void {
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setUser = useAuthStore((s) => s.setUser);
  const setHydrating = useAuthStore((s) => s.setHydrating);

  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      try {
        const response = await api.post<{ access_token: string; expires_in: number }>(
          "/v1/auth/refresh",
        );
        if (cancelled) return;
        setAccessToken(response.data.access_token);
        const user = await getCurrentUser();
        if (cancelled) return;
        setUser(user);
      } catch {
        if (cancelled) return;
        setAccessToken(null);
        setUser(null);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    }
    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [setAccessToken, setUser, setHydrating]);
}

export function useLogin() {
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setUser = useAuthStore((s) => s.setUser);

  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const tokens = await loginRequest(input);
      setAccessToken(tokens.access_token);
      const user = await getCurrentUser();
      setUser(user);
      return user;
    },
  });
}

export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  return useMutation({
    mutationFn: async () => {
      try {
        await logoutRequest();
      } finally {
        clear();
      }
    },
  });
}

export function useCurrentUser() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: getCurrentUser,
    enabled: isAuthenticated,
  });
}
